import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { IGetEmployeeByIdParams, ISapReservation } from "@/types"
import { TempLayout } from "@/layouts"
import TitleSection from "@/components/common/TitleSection"
import ReservationCard from "./components/ReservationCard"
import { useUserStore } from "@/stores/user/userStore"
import { useSapReservationStore } from "@/stores/sap-reservation/sapReservationStore"
import SapApprovalStatusFilter from "@/features/shared/components/SapApprovalStatusFilter"

const SapReservations = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const { reservations, meta, isLoading, fetchSapReservations } = useSapReservationStore()
  const [allReservations, setAllReservations] = useState<ISapReservation[]>([])
  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IGetEmployeeByIdParams>({
    page: 1,
    per_page: 4,
    employee_id: "",
  })

  const handleRefetch = useCallback(async () => {
    if (params.employee_id) {
      await fetchSapReservations(params)
    }
  }, [fetchSapReservations, params])

  const handleAddReservation = () => {
    navigate("/saprsv/create")
  }

  const handleApprovalStatusChange = (approvalStatus?: string) => {
    setAllReservations([])
    prevDataLengthRef.current = 0
    setParams(prev => ({
      ...prev,
      page: 1,
      approval_status: approvalStatus || undefined,
    }))
  }

  useEffect(() => {
    if (userData.employee_id) {
      setParams(prev => ({
        ...prev,
        employee_id: userData.employee_id,
      }))
    }
  }, [userData.employee_id])

  useEffect(() => {
    if (params.employee_id) {
      void fetchSapReservations(params)
    }
  }, [fetchSapReservations, params])

  useEffect(() => {
    if (params.page === 1) {
      setAllReservations(reservations)
      prevDataLengthRef.current = reservations.length
    } else if (reservations.length !== prevDataLengthRef.current) {
      setAllReservations(prev => {
        const existingIds = new Set(prev.map(item => item.record_id))
        const newItems = reservations.filter(item => !existingIds.has(item.record_id))
        return [...prev, ...newItems]
      })
      prevDataLengthRef.current = reservations.length
    }
  }, [reservations, params.page])

  const handleLoadMore = () => {
    if (meta && params.page < meta.total_pages) {
      setParams(prev => ({
        ...prev,
        page: prev.page + 1,
      }))
    }
  }

  return (
    <TempLayout title="SAP - Reservations" navNavigateUrl="/home">
      <TitleSection title="SAP - Reservations" onAdd={handleAddReservation} onRefresh={() => {
        void handleRefetch()
      }} />
      <SapApprovalStatusFilter value={params.approval_status} onChange={handleApprovalStatusChange} />

      <ReservationCard
        reservations={allReservations}
        isLoading={isLoading || !meta}
        handleReset={() => {
          setAllReservations([])
          prevDataLengthRef.current = 0
          setParams(prev => ({ ...prev, page: 1, approval_status: undefined }))
        }}
        onLoadMore={handleLoadMore}
        hasMore={meta ? params.page < meta.total_pages : true}
      />
    </TempLayout>
  )
}

export default SapReservations
